import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { isSFWContent, getInappropriateWordsFound, containsCoordinateKeywords, getCoordinateKeywordsFound } from '../utils/contentFilter';
import config from '../config';

interface RoomMember {
  user_id: number;
  username: string;
  is_host: boolean;
  has_position: boolean;
}

interface RoomInfo {
  room_id: number;
  room_name: string;
  room_key: string;
  grid_size: number;
  lives: number;
  is_started: boolean;
  is_host: boolean;
  members: RoomMember[];
  my_position?: { x: number; y: number };
}

interface Grid {
  x_axis: { [key: number]: string };
  y_axis: { [key: number]: string };
}

interface GuessedSquare {
  x: number;
  y: number;
  guessed_player_id: number;
  guesser_user_id: number;
  is_correct: boolean;
  guessed_at: string;
}

interface ChatMessage {
  id: number;
  message: string;
  username: string;
  created_at: string;
}

const GameRoom: React.FC = () => {
  console.log('🔄 GameRoom component rendered');
  const { roomKey } = useParams<{ roomKey: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [grid, setGrid] = useState<Grid | null>(null);
  const [guessedSquares, setGuessedSquares] = useState<GuessedSquare[]>([]);
  const [currentLives, setCurrentLives] = useState(0);
  const [myPosition, setMyPosition] = useState<{ x: number; y: number } | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [gamePhase, setGamePhase] = useState<'waiting' | 'playing' | 'ended'>('waiting');
  
  // New grid-based guessing state
  const [selectedCell, setSelectedCell] = useState<{ x: number; y: number } | null>(null);
  const [showPlayerSelection, setShowPlayerSelection] = useState(false);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [pendingVote, setPendingVote] = useState<{
    guessed_square_id: number;
    guesser: string;
    target_player: string;
    coordinates: { x: number; y: number };
  } | null>(null);

  // Ref to store current pendingVote state for WebSocket handlers
  const pendingVoteRef = useRef(pendingVote);
  useEffect(() => {
    pendingVoteRef.current = pendingVote;
  }, [pendingVote]);

  const [waitingForVotes, setWaitingForVotes] = useState<{
    guessed_square_id: number;
    target_player: string;
    coordinates: { x: number; y: number };
  } | null>(null);

  // Ref to store current waitingForVotes state for WebSocket handlers
  const waitingForVotesRef = useRef(waitingForVotes);
  useEffect(() => {
    waitingForVotesRef.current = waitingForVotes;
  }, [waitingForVotes]);

  // Debug wrapper for setWaitingForVotes
  const debugSetWaitingForVotes = (value: typeof waitingForVotes) => {
    console.log('🔄 setWaitingForVotes called with:', value);
    if (value === null) {
      console.log('📍 CLEARING waitingForVotes state');
    } else {
      console.log('📍 SETTING waitingForVotes state');
    }
    setWaitingForVotes(value);
  };
  const [votedWaiting, setVotedWaiting] = useState<{
    guessed_square_id: number;
    guesser: string;
    target_player: string;
    coordinates: { x: number; y: number };
  } | null>(null);

  // Ref to store current votedWaiting state for setTimeout callbacks and WebSocket handlers
  const votedWaitingRef = useRef(votedWaiting);
  useEffect(() => {
    votedWaitingRef.current = votedWaiting;
  }, [votedWaiting]);

  // Debug wrapper for setVotedWaiting
  const debugSetVotedWaiting = (value: typeof votedWaiting) => {
    console.log('🔄 setVotedWaiting called with:', value);
    if (value === null) {
      console.log('📍 CLEARING votedWaiting state');
      console.trace('Stack trace for clearing votedWaiting:');
    } else {
      console.log('📍 SETTING votedWaiting state');
    }
    setVotedWaiting(value);
  };

  // Debug wrapper for setSuccess
  const debugSetSuccess = (value: string) => {
    console.log('🔄 setSuccess called with:', value);
    setSuccess(value);
  };
  
  // Chat states
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatMessage, setChatMessage] = useState('');
  const [chatWarning, setChatWarning] = useState('');
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (roomKey) {
      fetchRoomInfo();
      connectWebSocket();
      fetchChatMessages();
    }
    
    return () => {
      if (wsRef.current) {
        // Clear ping interval if it exists
        if ((wsRef.current as any).pingInterval) {
          clearInterval((wsRef.current as any).pingInterval);
        }
        wsRef.current.close();
      }
    };
  }, [roomKey]);

  const connectWebSocket = () => {
    if (!roomKey || !token) return;

    try {
      const ws = new WebSocket(`${config.WS_BASE_URL}/ws/${roomKey}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      
      ws.onopen = () => {
        setWsConnected(true);
        setError('');
        
        // Start ping interval to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(pingInterval);
          }
        }, 30000);
        
        (ws as any).pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
        // Don't show error immediately, try to reconnect
        setTimeout(() => {
          if (!wsConnected) {
            setError('WebSocket connection error. Please refresh the page.');
          }
        }, 5000);
      };
      
      ws.onclose = (event) => {
        setWsConnected(false);
        
        // Clear ping interval
        if ((ws as any).pingInterval) {
          clearInterval((ws as any).pingInterval);
        }
        
        // Only show error for unexpected closures
        if (event.code !== 1000 && event.code !== 1001) {
          setError('WebSocket connection lost. Please refresh the page.');
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setError('Failed to connect to server. Please check if the backend is running.');
    }
  };

    const handleWebSocketMessage = (data: any) => {
      console.log('🔔 WebSocket message received:', data.type, data);
      console.log('Current waitingForVotes state:', waitingForVotes);
      console.log('🎯 handleWebSocketMessage called with type:', data.type);
      
      switch (data.type) {
      case 'pong':
        break;
      case 'your_position_updated':
        // Sent only to this player over their own connection - the room-wide
        // broadcasts never carry real coordinates, to keep positions secret.
        setMyPosition(data.position);
        break;
          case 'game_started':
            setGrid(data.grid);
            setCurrentLives(data.lives);
            setGamePhase('playing');
            setSuccess('Game started! Check your position and start guessing.');
            fetchRoomInfo();
            break;
          case 'player_joined':
            setSuccess(data.message);
            // Append the new member locally instead of re-fetching the whole room
            setRoomInfo(prev => {
              if (!prev) return prev;
              if (prev.members.some(m => m.user_id === data.user_id)) return prev;
              return {
                ...prev,
                members: [
                  ...prev.members,
                  { user_id: data.user_id, username: data.player, is_host: false, has_position: false }
                ]
              };
            });
            break;
          case 'vote_request':
            // Only show voting UI if this user is not the guesser
            if (data.guesser !== user?.username) {
              setPendingVote({
                guessed_square_id: data.guessed_square_id,
                guesser: data.guesser,
                target_player: data.target_player,
                coordinates: data.coordinates
              });
            }
            setSuccess(data.message);
            break;
          case 'vote_timeout':
          console.log('⏰ VOTE_TIMEOUT received for guessed_square_id:', data.guessed_square_id);
          console.log('Current waitingForVotes (from ref):', waitingForVotesRef.current);
          console.log('Current votedWaiting (from ref):', votedWaitingRef.current);
          setError(data.message);
          // Clear any pending vote if this was the one that timed out (using ref)
          if (pendingVoteRef.current && Number(pendingVoteRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
            console.log('✅ Clearing pendingVote due to timeout');
            setPendingVote(null);
          }
          // Clear voted waiting state if this was the one we voted on (using ref)
          if (votedWaitingRef.current && Number(votedWaitingRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
            console.log('✅ Clearing votedWaiting due to timeout');
            debugSetVotedWaiting(null);
          }
          // Clear waiting state if this was our guess (using ref)
          if (waitingForVotesRef.current && Number(waitingForVotesRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
            console.log('✅ Clearing waitingForVotes due to timeout');
            debugSetWaitingForVotes(null);
          }
          break;
        case 'guess_cancelled':
          console.log('=== GUESS_CANCELLED DEBUG ===');
          console.log('Received data:', data);
          console.log('Current waitingForVotes (from ref):', waitingForVotesRef.current);
          console.log('Current pendingVote (from ref):', pendingVoteRef.current);
          console.log('Current votedWaiting (from ref):', votedWaitingRef.current);
          console.log('Data guessed_square_id:', data.guessed_square_id, 'type:', typeof data.guessed_square_id);
          
          setSuccess(data.message);
          // Clear waiting state if this was our guess (using ref for current value)
          if (waitingForVotesRef.current && Number(waitingForVotesRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
            console.log('✅ CLEARING waitingForVotes due to guess_cancelled');
            debugSetWaitingForVotes(null);
          } else {
            console.log('❌ NOT clearing waitingForVotes - IDs do not match or state is null');
          }
          // Clear any pending vote if this was the one we were voting on (using ref for current value)
          if (pendingVoteRef.current && Number(pendingVoteRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
            console.log('✅ CLEARING pendingVote due to guess_cancelled');
            setPendingVote(null);
          } else {
            console.log('❌ NOT clearing pendingVote - IDs do not match or state is null');
          }
          // Clear voted waiting state if this was the one we voted on (using ref for current value)
          if (votedWaitingRef.current && Number(votedWaitingRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
            console.log('✅ CLEARING votedWaiting due to guess_cancelled');
            debugSetVotedWaiting(null);
          } else {
            console.log('❌ NOT clearing votedWaiting - IDs do not match or state is null');
          }
          console.log('=== END GUESS_CANCELLED DEBUG ===');
          break;
        case 'guess_rejected':
            setError(data.message);
            // Clear waiting state if this was our guess (using ref)
            if (waitingForVotesRef.current && Number(waitingForVotesRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
              debugSetWaitingForVotes(null);
            }
            // Clear any pending vote if this was the one we were voting on (using ref)
            if (pendingVoteRef.current && Number(pendingVoteRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
              setPendingVote(null);
            }
            // Clear voted waiting state if this was the one we voted on (using ref)
            if (votedWaitingRef.current && Number(votedWaitingRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
              console.log('✅ Clearing votedWaiting due to guess_rejected');
              debugSetVotedWaiting(null);
            }
            break;
          case 'square_guessed':
            console.log('=== SQUARE_GUESSED DEBUG ===');
            console.log('Received data:', data);
            console.log('Current waitingForVotes state (from ref):', waitingForVotesRef.current);
            console.log('Data guessed_square_id:', data.guessed_square_id, 'type:', typeof data.guessed_square_id);
            console.log('WaitingForVotes guessed_square_id:', waitingForVotesRef.current?.guessed_square_id, 'type:', typeof waitingForVotesRef.current?.guessed_square_id);
            console.log('Number comparison:', Number(waitingForVotesRef.current?.guessed_square_id), '===', Number(data.guessed_square_id));
            
            // Clear waiting state if this was our guess (using ref)
            if (waitingForVotesRef.current && Number(waitingForVotesRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
              console.log('✅ CLEARING waitingForVotes modal');
              debugSetWaitingForVotes(null);
            } else {
              console.log('❌ NOT clearing waitingForVotes - IDs do not match');
            }
            
            // Clear pending vote if this was the one we were voting on (using ref)
            if (pendingVoteRef.current && Number(pendingVoteRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
              console.log('✅ CLEARING pendingVote');
              setPendingVote(null);
            }
            
            // Clear voted waiting state if this was the one we voted on (using ref)
            console.log('🔍 About to check votedWaiting state...');
            console.log('Checking votedWaiting state (from ref):', votedWaitingRef.current);
            console.log('votedWaiting guessed_square_id:', votedWaitingRef.current?.guessed_square_id, 'type:', typeof votedWaitingRef.current?.guessed_square_id);
            console.log('data guessed_square_id:', data.guessed_square_id, 'type:', typeof data.guessed_square_id);
            console.log('Number comparison:', Number(votedWaitingRef.current?.guessed_square_id), '===', Number(data.guessed_square_id));
            
            if (votedWaitingRef.current && Number(votedWaitingRef.current.guessed_square_id) === Number(data.guessed_square_id)) {
              console.log('✅ CLEARING votedWaiting');
              debugSetVotedWaiting(null);
            } else {
              console.log('❌ NOT clearing votedWaiting - IDs do not match or state is null');
            }
            console.log('=== END SQUARE_GUESSED DEBUG ===');
            
            // Update lives
            if (data.lives_remaining !== undefined) {
              console.log('🔄 Updating currentLives to:', data.lives_remaining);
              setCurrentLives(data.lives_remaining);
            }
            
            if (data.is_correct) {
              if (data.repositioned) {
                setSuccess(`${data.guesser} correctly guessed ${data.target_player} at (${data.coordinates.x},${data.coordinates.y})! ${data.target_player} has moved to a new position.`);
                // The mover's own new coordinates arrive separately via the
                // your_position_updated message sent only to that player.
              } else {
                setSuccess(`${data.guesser} correctly guessed ${data.target_player} at (${data.coordinates.x},${data.coordinates.y})! Game complete - all squares guessed!`);
              }
            } else {
              // Incorrect guess
              let message = `${data.guesser} guessed ${data.target_player} at (${data.coordinates.x},${data.coordinates.y}) - Incorrect!`;

              if (data.repositioned_players && data.repositioned_players.length > 0) {
                const playerNames = data.repositioned_players.map((p: {username: string}) => p.username).join(', ');
                message += ` Players at that location (${playerNames}) have been repositioned!`;
                // Each repositioned player's own new coordinates arrive separately
                // via their own your_position_updated message.
              }

              if (data.lives_lost > 0) {
                message += ` Lives lost: ${data.lives_lost}. Lives remaining: ${data.lives_remaining}`;
              }

              setError(message);
            }

            // Check for game completion or game over
            if (data.game_complete) {
              setGamePhase('ended');
              // Show victory modal if game completed with lives remaining
              if (data.lives_remaining > 0) {
                setShowVictoryModal(true);
              }
            }

            if (data.game_over) {
              setGamePhase('ended');
              setShowGameOverModal(true);
              if (data.reason === 'insufficient_players') {
                setError('Game ended: Not enough players remaining');
              }
            }

            // Append the newly-guessed square locally instead of re-fetching the whole list
            setGuessedSquares(prev => [
              ...prev,
              {
                x: data.coordinates.x,
                y: data.coordinates.y,
                guessed_player_id: data.target_user_id,
                guesser_user_id: data.guesser_user_id,
                is_correct: data.is_correct,
                guessed_at: new Date().toISOString()
              }
            ]);
            break;
      case 'guess_result':
        if (data.correct) {
          setSuccess(data.message);
        } else {
          setCurrentLives(data.lives_remaining);
          setError(data.message);
        }
        break;
      case 'game_over':
        setGamePhase('ended');
        setError(data.message);
        break;
      case 'echo':
        break;
      case 'error':
        setError(data.message || 'WebSocket error');
        break;
      case 'chat_message':
        // Add new chat message to the list
        setChatMessages(prev => [...prev, {
          id: data.message_id,
          message: data.message,
          username: data.username,
          created_at: data.created_at
        }]);
        break;
      default:
        console.log('Unknown WebSocket message:', data);
    }
  };

  const fetchRoomInfo = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/room/${roomKey}`);
      console.log('Fetched room info:', response.data);
      setRoomInfo(response.data);
      setCurrentLives(response.data.lives);
      
      // Set player's position if available
      if (response.data.my_position) {
        console.log('Setting my position to:', response.data.my_position);
        setMyPosition(response.data.my_position);
      } else {
        console.log('No my_position in response');
      }
      
      // Set game phase based on room state
      if (response.data.is_started) {
        setGamePhase('playing');
        // Fetch grid if game is already started
        if (response.data.game_state?.grid) {
          setGrid(response.data.game_state.grid);
        }
        // Fetch guessed squares
        await fetchGuessedSquares();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch room info');
    }
  };

  const fetchGuessedSquares = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/room/${roomKey}/guessed_squares`);
      setGuessedSquares(response.data.guessed_squares);
    } catch (err: any) {
      console.error('Failed to fetch guessed squares:', err);
    }
  };

  const handleStartGame = async () => {
    if (!roomKey) return;
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('room_key', roomKey);
      
      await axios.post(`${config.API_BASE_URL}/start_game`, formData);
      setSuccess('Game started successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  const handleGridCellClick = (x: number, y: number) => {
    if (gamePhase !== 'playing') return;
    
    setSelectedCell({ x, y });
    setShowPlayerSelection(true);
  };

  const handleCancelGuess = async () => {
    if (!waitingForVotes) return;
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('guessed_square_id', waitingForVotes.guessed_square_id.toString());
      
      await axios.post(`${config.API_BASE_URL}/cancel_guess`, formData);
      
      setSuccess('Guess cancelled successfully');
      debugSetWaitingForVotes(null);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to cancel guess');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (vote: boolean) => {
    if (!pendingVote) return;
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('guessed_square_id', pendingVote.guessed_square_id.toString());
      formData.append('vote', vote.toString());
      
      const response = await axios.post(`${config.API_BASE_URL}/vote_on_guess`, formData);
      
      if (response.data.approved !== undefined) {
        // Vote completed
        if (response.data.approved) {
          setSuccess(`Vote recorded! ${response.data.vote_result}. Guess approved and processed.`);
          
          // Update lives if provided
          if (response.data.lives_remaining !== undefined) {
            setCurrentLives(response.data.lives_remaining);
          }
          
          // Check for game over
          if (response.data.game_over) {
            setGamePhase('ended');
            setShowGameOverModal(true);
          }

          // If we were the one repositioned, our new coordinates arrive separately
          // via the your_position_updated WebSocket message sent only to us.
        } else {
          setSuccess(`Vote recorded! ${response.data.vote_result}. Guess rejected.`);
        }
        
        // Store the guessed_square_id before clearing pendingVote
        const guessedSquareId = pendingVote.guessed_square_id;
        setPendingVote(null);
        // Don't clear votedWaiting here - let the square_guessed WebSocket message handle it
        // debugSetVotedWaiting(null);
        // Also clear waitingForVotes if this was our guess
        if (waitingForVotes && waitingForVotes.guessed_square_id === guessedSquareId) {
          console.log('Clearing waitingForVotes modal in handleVote fallback');
          debugSetWaitingForVotes(null);
        }
      } else {
        // Still waiting for more votes - show spinner
        debugSetVotedWaiting({
          guessed_square_id: pendingVote.guessed_square_id,
          guesser: pendingVote.guesser,
          target_player: pendingVote.target_player,
          coordinates: pendingVote.coordinates
        });
        console.log('🔍 votedWaiting state set, checking if it gets cleared...');
        setTimeout(() => {
          console.log('🔍 votedWaiting state after 100ms:', votedWaitingRef.current);
          console.log('🔍 Current votedWaiting state in setTimeout:', votedWaitingRef.current);
        }, 100);
        setPendingVote(null);
        debugSetSuccess(`Vote recorded! ${response.data.current_tally}. Waiting for ${response.data.votes_needed - response.data.votes_received} more vote(s).`);
      }
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit vote');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerSelect = async (targetUserId: number) => {
    console.log('🎯 handlePlayerSelect called with targetUserId:', targetUserId);
    console.log('Current roomKey:', roomKey);
    console.log('Current selectedCell:', selectedCell);
    
    if (!roomKey || !selectedCell) {
      console.log('❌ Early return: missing roomKey or selectedCell');
      return;
    }
    
    console.log('✅ Starting guess submission...');
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('room_key', roomKey);
      formData.append('target_user_id', targetUserId.toString());
      formData.append('x_guess', selectedCell.x.toString());
      formData.append('y_guess', selectedCell.y.toString());
      
      console.log('🚀 Submitting guess with formData:', {
        room_key: roomKey,
        target_user_id: targetUserId,
        x_guess: selectedCell.x,
        y_guess: selectedCell.y
      });
      
      const response = await axios.post(`${config.API_BASE_URL}/guess_coordinate`, formData);
      
      console.log('📥 Guess response received:', response.data);
      
      if (response.data.requires_voting) {
          // Voting is required, the guess is pending
          console.log('=== SETTING waitingForVotes ===');
          console.log('Response data:', response.data);
          console.log('Setting guessed_square_id:', response.data.guessed_square_id);
          console.log('Current waitingForVotes before setting:', waitingForVotes);
          debugSetWaitingForVotes({
            guessed_square_id: response.data.guessed_square_id,
            target_player: response.data.target_player,
            coordinates: { x: selectedCell.x, y: selectedCell.y }
          });
          console.log('=== END SETTING waitingForVotes ===');
          setSelectedCell(null);
          setShowPlayerSelection(false);
          return;
        }
          
          // For 2-player games, process immediately
          // Update lives from response
          if (response.data.lives_remaining !== undefined) {
            setCurrentLives(response.data.lives_remaining);
          }
          
          if (response.data.is_correct) {
            if (response.data.repositioned) {
              setSuccess(`Correct! ${response.data.target_player} was at (${selectedCell.x}, ${selectedCell.y}) and has moved to a new position!`);
              // Our own WebSocket connection also receives the square_guessed broadcast
              // for this guess, which applies any guessed-square updates locally.
            } else {
              setSuccess(`Correct! ${response.data.target_player} was at (${selectedCell.x}, ${selectedCell.y})! Game complete - all squares guessed!`);
            }
          } else {
            // Incorrect guess
            let message = `Incorrect! ${response.data.target_player} is not at (${selectedCell.x}, ${selectedCell.y})`;

            if (response.data.repositioned_players && response.data.repositioned_players.length > 0) {
              const playerNames = response.data.repositioned_players.map((p: any) => p.username).join(', ');
              message += ` Players at that location (${playerNames}) have been repositioned!`;
            }

            if (response.data.lives_lost > 0) {
              message += ` Lives lost: ${response.data.lives_lost}. Lives remaining: ${response.data.lives_remaining}`;
            }

            setError(message);
          }

          // Check for game over
          if (response.data.game_over) {
            setGamePhase('ended');
            setShowGameOverModal(true);
          }

      // Reset selection
      setSelectedCell(null);
      setShowPlayerSelection(false);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit guess');
    } finally {
      setLoading(false);
    }
  };

  // Chat functions
  const fetchChatMessages = async () => {
    if (!roomKey) return;
    
    try {
      const response = await axios.get(`${config.API_BASE_URL}/room/${roomKey}/chat`);
      setChatMessages(response.data.messages);
    } catch (err: any) {
      console.error('Failed to fetch chat messages:', err);
    }
  };

  const handleChatMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMessage = e.target.value;
    setChatMessage(newMessage);
    
    // Real-time validation
    if (newMessage) {
      // Check SFW content
      if (!isSFWContent(newMessage)) {
        const inappropriateWords = getInappropriateWordsFound(newMessage);
        setChatWarning(`⚠️ Inappropriate content detected: ${inappropriateWords.join(', ')}`);
        return;
      }
      
      // Check coordinate keywords if game is started and grid is available
      if (gamePhase === 'playing' && grid && containsCoordinateKeywords(newMessage, grid)) {
        const coordinateWords = getCoordinateKeywordsFound(newMessage, grid);
        setChatWarning(`⚠️ Coordinate keywords detected: ${coordinateWords.join(', ')}`);
        return;
      }
    }
    
    setChatWarning('');
  };

  const sendChatMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomKey || !chatMessage.trim() || chatWarning) return;
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('message', chatMessage.trim());
      
      await axios.post(`${config.API_BASE_URL}/room/${roomKey}/chat`, formData);
      setChatMessage('');
      setChatWarning('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Debug useEffect to track votedWaiting state changes
  useEffect(() => {
    console.log('🔍 votedWaiting state changed to:', votedWaiting);
  }, [votedWaiting]);

  const retryWebSocketConnection = () => {
    if (wsRef.current) {
      if ((wsRef.current as any).pingInterval) {
        clearInterval((wsRef.current as any).pingInterval);
      }
      wsRef.current.close();
    }
    setWsConnected(false);
    setError('');
    connectWebSocket();
  };

  if (!roomInfo) {
    return <div className="loading">Loading room...</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>{roomInfo.room_name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              padding: '5px 10px',
              borderRadius: '15px',
              backgroundColor: wsConnected ? 'rgba(0, 184, 148, 0.2)' : 'rgba(255, 107, 107, 0.2)',
              border: `1px solid ${wsConnected ? '#00b894' : '#ff6b6b'}`
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: wsConnected ? '#00b894' : '#ff6b6b'
              }}></div>
              <span style={{ fontSize: '12px', color: wsConnected ? '#00b894' : '#ff6b6b' }}>
                {wsConnected ? 'Connected' : 'Disconnected'}
              </span>
              {!wsConnected && (
                <button 
                  onClick={retryWebSocketConnection}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ff6b6b',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textDecoration: 'underline'
                  }}
                >
                  Retry
                </button>
              )}
            </div>
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">Back to Dashboard</button>
          </div>
        </div>
        
        <div className="game-info">
          <div className="info-card">
            <h3>Room Key</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffd700', margin: 0 }}>
                {roomInfo.room_key}
              </p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(roomInfo.room_key);
                  setSuccess('Room key copied to clipboard!');
                  setTimeout(() => setSuccess(''), 2000);
                }}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                title="Copy room key"
              >
                📋 Copy
              </button>
            </div>
          </div>
          <div className="info-card">
            <h3>Grid Size</h3>
            <p>{roomInfo.grid_size}x{roomInfo.grid_size}</p>
          </div>
          <div className="info-card">
            <h3>Lives</h3>
            <p>{currentLives}</p>
          </div>
        </div>

        {/* Player's Position */}
        {myPosition && gamePhase === 'playing' && (
          <div className="card" style={{ background: 'rgba(0, 184, 148, 0.2)', border: '2px solid #00b894' }}>
            <h2>🎯 Your Position</h2>
            <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#00b894' }}>
              {grid && (
                <p style={{ fontSize: '20px', color: '#fff' }}>
                  <strong>{grid.x_axis[myPosition.x]}</strong> × <strong>{grid.y_axis[myPosition.y]}</strong>
                </p>
              )}
            </div>
            <p style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px', opacity: 0.8 }}>
              Keep this secret! Other players will try to guess your position.
            </p>
          </div>
        )}
        
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {/* Room Members */}
        <div className="card">
          <h2>Room Members ({roomInfo.members.length})</h2>
          <div className="room-members">
            {roomInfo.members.map((member) => (
              <div key={member.user_id} className="member-card">
                <div className="member-info">
                  <span className="member-name">{member.username}</span>
                  {member.is_host && <span className="host-badge">👑 Host</span>}
                  {member.has_position && <span className="ready-badge">✅ Ready</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Start Game Button */}
        {roomInfo.is_host && gamePhase === 'waiting' && (
          <div className="card">
            <h2>Start Game</h2>
            <p>All players are ready. Click below to start the game!</p>
            <button 
              onClick={handleStartGame} 
              disabled={loading}
              className="btn"
            >
              {loading ? 'Starting...' : 'Start Game'}
            </button>
          </div>
        )}

        {/* Game Grid Preview (when not started) */}
        {!grid && gamePhase === 'waiting' && (
          <div className="card">
            <h2>Grid Preview</h2>
            <p style={{ marginBottom: '20px', textAlign: 'center', fontSize: '16px' }}>
              💡 This is what the grid will look like when the game starts
            </p>
            
            <div className="grid-container" style={{ '--grid-size': roomInfo.grid_size } as React.CSSProperties}>
              <div className="grid-wrapper">
                {/* X-axis labels */}
                <div className="grid-header" style={{ '--grid-size': roomInfo.grid_size } as React.CSSProperties}>
                  {Array.from({ length: roomInfo.grid_size }, (_, x) => (
                    <div key={`x-${x}`} className="grid-header-label">
                      Word {x + 1} {/* Convert to 1-based */}
                    </div>
                  ))}
                </div>
                
                {/* Main grid area */}
                <div className="grid-main">
                  {/* Y-axis labels */}
                  <div className="grid-labels-y">
                    {Array.from({ length: roomInfo.grid_size }, (_, y) => (
                      <div key={y} className="grid-label">
                        Word {y + 1} {/* Convert to 1-based */}
                      </div>
                    ))}
                  </div>
                  
                  {/* Grid cells */}
                  <div className="grid" style={{ '--grid-size': roomInfo.grid_size } as React.CSSProperties}>
                    {Array.from({ length: roomInfo.grid_size }, (_, y) => 
                      Array.from({ length: roomInfo.grid_size }, (_, x) => (
                        <div 
                          key={`${x + 1}-${y + 1}`} 
                          className="grid-cell"
                          style={{ 
                            cursor: 'default',
                            opacity: 0.6
                          }}
                          title={`${x + 1},${y + 1}`}
                        >
                          ⚪
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Game Grid */}
        {grid && gamePhase === 'playing' && (
          <div className="card">
            <h2>Game Grid</h2>
            <p style={{ marginBottom: '20px', textAlign: 'center', fontSize: '16px' }}>
              💡 Click on any cell to guess that position for a player
            </p>
            
            <div className="grid-container" style={{ '--grid-size': roomInfo.grid_size } as React.CSSProperties}>
              <div className="grid-wrapper">
                {/* X-axis labels */}
                <div className="grid-header" style={{ '--grid-size': roomInfo.grid_size } as React.CSSProperties}>
                  {Array.from({ length: roomInfo.grid_size }, (_, x) => (
                    <div key={`x-${x}`} className="grid-header-label">
                      {grid.x_axis[x + 1]} {/* Convert to 1-based */}
                    </div>
                  ))}
                </div>
                
                {/* Main grid area */}
                <div className="grid-main">
                  {/* Y-axis labels */}
                  <div className="grid-labels-y">
                    {Array.from({ length: roomInfo.grid_size }, (_, y) => (
                      <div key={y} className="grid-label">
                        {grid.y_axis[y + 1]} {/* Convert to 1-based */}
                      </div>
                    ))}
                  </div>
                  
                  {/* Grid cells */}
                  <div className="grid" style={{ '--grid-size': roomInfo.grid_size } as React.CSSProperties}>
                    {Array.from({ length: roomInfo.grid_size }, (_, y) => 
                      Array.from({ length: roomInfo.grid_size }, (_, x) => {
                        const actualX = x + 1; // Convert to 1-based
                        const actualY = y + 1; // Convert to 1-based
                        const guessedSquare = guessedSquares.find(square => square.x === actualX && square.y === actualY);
                        const isGuessed = !!guessedSquare;
                        const isCorrect = guessedSquare?.is_correct;
                        
                        return (
                          <div 
                            key={`${actualX}-${actualY}`} 
                            className={`grid-cell ${isGuessed ? (isCorrect ? 'correct' : 'incorrect') : ''}`}
                            onClick={() => !isGuessed && gamePhase === 'playing' ? handleGridCellClick(actualX, actualY) : null}
                            style={{ 
                              cursor: gamePhase === 'playing' && !isGuessed ? 'pointer' : 'default',
                              opacity: gamePhase === 'playing' ? (isGuessed ? 0.7 : 1) : 0.6,
                              backgroundColor: isGuessed ? (isCorrect ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.3)' ) : undefined
                            }}
                            title={gamePhase === 'playing' ? 
                              (isGuessed ? `${actualX},${actualY} - Already guessed (${isCorrect ? 'Correct' : 'Incorrect'})` : `Click to guess ${actualX},${actualY}`) : 
                              isGuessed ? `${actualX},${actualY} - ${isCorrect ? 'Correct' : 'Incorrect'}` : ''
                            }
                          >
                            {isGuessed ? (
                              isCorrect ? '✅' : '❌'
                            ) : (
                              '⚪'
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Victory Modal */}
        {showVictoryModal && (
          <>
            {/* Backdrop overlay */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 999
            }} />
            
            {/* Modal */}
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.95) 0%, rgba(255, 165, 0, 0.95) 100%)', 
              border: '3px solid #ffd700',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              maxWidth: '500px',
              textAlign: 'center',
              color: 'black',
              boxShadow: '0 0 30px rgba(255, 215, 0, 0.8)'
            }}>
            <h2 style={{ color: 'black', fontSize: '2.5em', marginBottom: '20px', textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
              🎉 VICTORY! 🎉
            </h2>
            <p style={{ fontSize: '20px', marginBottom: '15px', fontWeight: 'bold' }}>
              All squares guessed!
            </p>
            <p style={{ fontSize: '18px', marginBottom: '20px' }}>
              Lives remaining: <strong>{currentLives}</strong>
            </p>
            <p style={{ fontSize: '16px', marginBottom: '25px', fontStyle: 'italic' }}>
              Congratulations! You've successfully completed the grid!
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button 
                onClick={() => navigate('/dashboard')} 
                className="btn"
                style={{ 
                  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  padding: '12px 30px'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
          </>
        )}
        
        {/* Game Over Modal */}
        {showGameOverModal && (
          <>
            {/* Backdrop overlay */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 999
            }} />
            
            {/* Modal */}
            <div className="card" style={{ 
              background: 'rgba(255, 0, 0, 0.9)', 
              border: '3px solid #ff0000',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              maxWidth: '400px',
              textAlign: 'center',
              color: 'black',
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.5)'
            }}>
            <h2 style={{ color: 'black', fontSize: '2em', marginBottom: '20px' }}>💀 GAME OVER</h2>
            <p style={{ fontSize: '18px', marginBottom: '20px' }}>
              No lives remaining! The game has ended.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button 
                onClick={() => navigate('/dashboard')} 
                className="btn"
                style={{ background: '#ff6b6b' }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
          </>
        )}
        
        {/* Player Selection Modal */}
        {showPlayerSelection && selectedCell && (
          <>
            {/* Backdrop overlay */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 999
            }} />
            
            {/* Modal */}
            <div className="card" style={{ 
              background: '#f5f5f5', 
              border: '2px solid #ccc',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              maxWidth: '400px',
              textAlign: 'center',
              color: 'black',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)'
            }}>
            <h2>🎯 Select Player</h2>
            <p style={{ textAlign: 'center', fontSize: '18px', marginBottom: '20px' }}>
              You selected: <strong>{grid?.x_axis[selectedCell.x]} / {grid?.y_axis[selectedCell.y]}</strong>
            </p>
            <p style={{ textAlign: 'center', marginBottom: '20px' }}>
              Which player do you think has this position?
            </p>
            
            <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
              {roomInfo.members
                .filter(member => member.user_id !== user?.id) // Don't show yourself
                .map((member) => (
                  <button
                    key={member.user_id}
                    onClick={() => handlePlayerSelect(member.user_id)}
                    className="btn"
                    style={{ width: '100%', padding: '15px' }}
                  >
                    {member.username}
                  </button>
                ))}
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <button 
                onClick={() => {
                  setShowPlayerSelection(false);
                  setSelectedCell(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
          </>
        )}
        
        {gamePhase === 'ended' && (
          <div className="card">
            <h2>🎉 Game Complete!</h2>
            <p>All squares on the grid have been guessed! The game is over.</p>
            <button onClick={() => navigate('/dashboard')} className="btn">
              Back to Dashboard
            </button>
          </div>
        )}

        {/* Voting Modal */}
        {pendingVote && (
          <>
            {/* Backdrop overlay */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 999
            }} />
            
            {/* Modal */}
            <div className="card" style={{ 
              background: '#f5f5f5', // Light grey background
              border: '2px solid #ccc', // Grey border
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              maxWidth: '500px',
              textAlign: 'center',
              color: 'black',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)' // Subtle black shadow
            }}>
            <h2>🗳️ Vote on Guess</h2>
            <div style={{ 
              background: 'rgba(0, 0, 0, 0.1)', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '2px solid #ddd'
            }}>
              <p style={{ textAlign: 'center', fontSize: '18px', marginBottom: '10px' }}>
                <strong>{pendingVote.guesser}</strong> guessed that <strong>{pendingVote.target_player}</strong> is at:
              </p>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                {grid && (
                  <>
                    <strong>{grid.x_axis[pendingVote.coordinates.x]}</strong> × <strong>{grid.y_axis[pendingVote.coordinates.y]}</strong>
                  </>
                )}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#666',
                textAlign: 'center',
                marginTop: '5px'
              }}>
                Position ({pendingVote.coordinates.x}, {pendingVote.coordinates.y})
              </div>
            </div>
            <p style={{ textAlign: 'center', marginBottom: '30px', fontSize: '16px' }}>
              Do you approve this guess?
            </p>
            
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '20px' }}>
              <button
                onClick={() => handleVote(true)}
                className="btn"
                style={{ 
                  background: '#4CAF50', 
                  color: 'white',
                  padding: '15px 30px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
                disabled={loading}
              >
                ✅ YES
              </button>
              <button
                onClick={() => handleVote(false)}
                className="btn"
                style={{ 
                  background: '#f44336', 
                  color: 'white',
                  padding: '15px 30px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
                disabled={loading}
              >
                ❌ NO
              </button>
            </div>
          </div>
          </>
        )}

        {/* Waiting for Votes Modal */}
        {waitingForVotes && (
          <>
            {/* Backdrop overlay */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 999
            }} />
            
            {/* Modal */}
            <div className="card" style={{ 
              background: '#f5f5f5', // Light grey background
              border: '2px solid #ccc', // Grey border
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              maxWidth: '500px',
              textAlign: 'center',
              color: 'black',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)' // Subtle black shadow
            }}>
            <h2>⏳ Waiting for Votes</h2>
            <p style={{ textAlign: 'center', fontSize: '18px', marginBottom: '20px' }}>
              You guessed that <strong>{waitingForVotes.target_player}</strong> is at:
            </p>
            <div style={{ 
              background: 'rgba(0, 0, 0, 0.1)', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '2px solid #ddd'
            }}>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                {grid && (
                  <>
                    <strong>{grid.x_axis[waitingForVotes.coordinates.x]}</strong> × <strong>{grid.y_axis[waitingForVotes.coordinates.y]}</strong>
                  </>
                )}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#666',
                textAlign: 'center',
                marginTop: '5px'
              }}>
                Position ({waitingForVotes.coordinates.x}, {waitingForVotes.coordinates.y})
              </div>
            </div>
            <p style={{ textAlign: 'center', marginBottom: '30px', fontSize: '16px' }}>
              Other players are voting on your guess...
            </p>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <button 
                onClick={handleCancelGuess}
                className="btn btn-secondary"
                disabled={loading}
                style={{ 
                  background: '#e74c3c', 
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Cancelling...' : 'Cancel Guess'}
              </button>
            </div>
          </div>
          </>
        )}

        {/* Voted Waiting Modal */}
        {votedWaiting && (
          <>
            {/* Backdrop overlay */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 999
            }} />
            
            {/* Modal */}
            <div className="card" style={{ 
              background: '#f5f5f5', // Light grey background
              border: '2px solid #ccc', // Grey border
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              maxWidth: '500px',
              textAlign: 'center',
              color: 'black',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)' // Subtle black shadow
            }}>
            <h2>🗳️ Vote Submitted</h2>
            <div style={{ 
              background: 'rgba(0, 0, 0, 0.1)', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '2px solid #ddd'
            }}>
              <p style={{ textAlign: 'center', fontSize: '18px', marginBottom: '10px' }}>
                <strong>{votedWaiting.guesser}</strong> guessed that <strong>{votedWaiting.target_player}</strong> is at:
              </p>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                {grid && (
                  <>
                    <strong>{grid.x_axis[votedWaiting.coordinates.x]}</strong> × <strong>{grid.y_axis[votedWaiting.coordinates.y]}</strong>
                  </>
                )}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#666',
                textAlign: 'center',
                marginTop: '5px'
              }}>
                Position ({votedWaiting.coordinates.x}, {votedWaiting.coordinates.y})
              </div>
            </div>
            <p style={{ textAlign: 'center', marginBottom: '30px', fontSize: '16px' }}>
              Your vote has been recorded. Waiting for other players to vote...
            </p>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          </div>
          </>
        )}
      </div>
      
      {/* Chat Section */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h2>💬 Chat</h2>
        <div style={{ 
          height: '300px', 
          border: '1px solid #ccc', 
          borderRadius: '8px', 
          padding: '10px', 
          marginBottom: '10px',
          overflowY: 'auto',
          backgroundColor: '#f9f9f9'
        }}>
          {chatMessages.map((msg) => (
            <div key={msg.id} style={{ marginBottom: '8px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '2px'
              }}>
                <strong style={{ color: '#2c3e50' }}>{msg.username}</strong>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {new Date(msg.created_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </span>
              </div>
              <div style={{ color: '#333' }}>{msg.message}</div>
            </div>
          ))}
          <div ref={chatMessagesEndRef} />
        </div>
        
        <form onSubmit={sendChatMessage} style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={chatMessage}
            onChange={handleChatMessageChange}
            placeholder="Type your message..."
            style={{ 
              flex: 1, 
              padding: '10px', 
              border: '1px solid #ccc', 
              borderRadius: '4px',
              borderColor: chatWarning ? '#ff4444' : '#ccc',
              backgroundColor: chatWarning ? '#ffe6e6' : 'white'
            }}
            disabled={loading}
          />
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading || !chatMessage.trim() || !!chatWarning}
            style={{
              opacity: (loading || !chatMessage.trim() || !!chatWarning) ? 0.6 : undefined,
              cursor: (loading || !chatMessage.trim() || !!chatWarning) ? 'not-allowed' : undefined
            }}
          >
            Send
          </button>
        </form>
        
        {chatWarning && (
          <div style={{ 
            color: '#ff4444', 
            fontSize: '12px', 
            marginTop: '5px',
            fontWeight: 'bold'
          }}>
            {chatWarning}
          </div>
        )}
      </div>
    </div>
  );
};

export default GameRoom;